import axios from 'axios'
import * as types from '../mutation-types'
import _ from 'lodash'
import Vue from 'vue'

// namespaced
export const namespaced = true

// state
export const state = {
  msg: null,
  points: null,
  profile: null,
  history: [],
  inbox: [],
  membership: null,
  membershipConfig: null,
  mcart: [],
  activeOrderMembership: null
}

// getters
export const getters = {
  memberMsg: state => state.msg,
  points: state => state.points,
  profile: state => state.profile,
  history: state => state.history,
  isMember: state => state.profile !== null && state.profile.loyalty_member_id,
  inbox: state => state.inbox,
  membership: state => state.membership,
  membershipConfig: state => state.membershipConfig,
  activeOrderMembership: state => state.activeOrderMembership,

  activeOrderPaymentsMembership: (state, getters, rootState) => {
    const paymentAmount = parseFloat((Math.round(((state.activeOrderMembership ? parseFloat(state.activeOrderMembership.payable_amount) : 0) + (state.activeOrderMembership ? parseFloat(state.activeOrderMembership.payments_discount) : 0)) * 100) / 100).toFixed(2))
    let paymentPoints
    if (!state.usePoints || !rootState.member.points) {
      paymentPoints = 0
    } else {
      const points = parseFloat(rootState.member.points)
      if (points === 0) {
        paymentPoints = 0
      } else if (points < paymentAmount) {
        paymentPoints = points
      } else {
        paymentPoints = paymentAmount
      }
    }
    return {
      points: paymentPoints,
      credits: 0,
      topay_payment: parseFloat(Math.round((paymentAmount - paymentPoints) * 100) / 100).toFixed(2)
    }
  }
}

// mutations
export const mutations = {
  [types.RESET_MEMBER_DATA] (state) {
    Object.assign(state, {
      msg: null,
      points: null,
      profile: null,
      history: [],
      inbox: [],
      membership: null,
      membershipConfig: null,
      mcart: [],
      activeOrderPaymentsMembership: null
    })
  },

  [types.SET_MEMBER_MSG] (state, msg) {
    const msgs = {
      point: 'Failed to fetch points data',
      history: 'Failed to fetch points history',
      profile: 'Failed to fetch member profile',
      inbox: 'Failed to fetch member inbox'
    }
    const message = msg ? (msgs[msg] ? msgs[msg] : msg) : 'Failed to fetch data'
    state.msg = message
  },

  [types.RESET_MEMBER_MSG] (state) {
    state.msg = null
  },

  [types.FETCH_POINTS] (state, points) {
    state.points = points
  },

  [types.FETCH_PROFILE] (state, profile) {
    state.profile = profile
    state.points = profile.points
  },

  [types.UPDATE_PROFILE] (state, profile) {
    state.profile = Object.assign({}, state.profile, profile)
  },

  [types.FETCH_INBOX] (state, inbox) {
    state.inbox = inbox
  },

  [types.FETCH_MEMBERSHIP] (state, payload) {
    state.membership = payload
  },

  [types.FETCH_MEMBERSHIP_CONFIG] (state, payload) {
    state.membershipConfig = payload
  },

  [types.ADD_MEMBERSHIP_ITEM] (state, newItem) {
    // payload is item
    const i = state.mcart.findIndex(item => (parseInt(item.item_id) === parseInt(newItem.item_id)) &&
      (_.isEqual(_.omit(item, ['quantity', 'cart_id']), _.omit(newItem, ['quantity', 'cart_id'])) === true))
    if (i === -1) {
      state.mcart.push(newItem)
    } else {
      const qty = (state.mcart[i].voucher_id) ? 1 : state.mcart[i].quantity + newItem.quantity
      const item = Object.assign({}, state.mcart[i], {
        quantity: qty
      })
      Vue.set(state.mcart, i, item)
    }
  },

  [types.RESET_MEMBERSHIP_ITEM] (state) {
    state.mcart = []
  },

  [types.CHECK_FINAL_MEMBERSHIP_ORDER] (state, payload) {
    const amount = payload.total.replace(/[^\d.-]/g, '')
    const discount = payload.discount_total.replace(/[^\d.-]/g, '')
    const topay = payload.payable.replace(/[^\d.-]/g, '')
    const points = parseFloat(Math.round((parseFloat(amount) + parseFloat(discount) - parseFloat(topay)) * 100) / 100)
    delete payload.code
    state.activeOrderMembership = Object.assign({}, state.activeOrderMembership, payload, {
      payable_amount: amount,
      payments_discount: discount,
      payments_topay: topay,
      payments_points: points,
      payments_credit: 0
    })
  }
}

// actions
export const actions = {
  resetMemberData ({ commit }) {
    commit(types.RESET_MEMBER_DATA)
  },

  async fetchProfile ({ commit, rootState, rootGetters }) {
    commit(types.RESET_MEMBER_MSG)
    try {
      const { data } = await axios.post('/api/users/profile', {
        loyalty_group_id: parseInt(rootGetters['config/loyaltyGroupId'])
      })
      if (data.code === 1) {
        commit(types.FETCH_PROFILE, data.member)
      } else {
        commit(types.SET_MEMBER_MSG, 'profile')
      }
    } catch (e) {
      commit(types.SET_MEMBER_MSG, 'profile')
    }
  },

  async updateProfile ({ commit }, payload) {
    // payload is member
    commit(types.RESET_MEMBER_MSG)
    commit(types.UPDATE_PROFILE, payload)
  },

  async fetchInbox ({ commit, rootGetters }) {
    commit(types.RESET_MEMBER_MSG)
    try {
      const { data } = await axios.post('/api/member/inbox', {
        group_id: parseInt(rootGetters['config/gid']),
        loyalty_group_id: parseInt(rootGetters['config/loyaltyGroupId']),
        restaurant_id: parseInt(rootGetters['config/rid']) === 0 ? parseInt(rootGetters['config/rid2']) : parseInt(rootGetters['config/rid'])
      })
      if (data) {
        commit(types.FETCH_INBOX, data)
      } else {
        commit(types.SET_MEMBER_MSG, 'inbox')
      }
    } catch (e) {
      commit(types.SET_MEMBER_MSG, 'inbox')
    }
  },

  async markMailAsRead ({ commit }, mail) {
    mail.status = 1
    try {
      const { data } = await axios.post('/api/mail/status/update', {
        notification_id: mail.id
      })
    } catch (e) { }
  },

  async fetchMembership ({ commit, rootGetters }) {
    try {
      await axios.post('/api/users/membership', {
        loyalty_group_id: parseInt(rootGetters['config/loyaltyGroupId'])
      }).then(function (response) {
        commit(types.FETCH_MEMBERSHIP, response.data.membership)
      })
    } catch (e) { }
  },

  async fetchMembershipConfig ({ commit, state, rootGetters }) {
    try {
      await axios.post('/api/users/membership_config', {
        restaurant_id: parseInt(rootGetters['config/rid']),
        rate_id: parseInt(state.membership.rate_id)
      }).then(function (response) {
        commit(types.FETCH_MEMBERSHIP_CONFIG, response.data)
      })
    } catch (e) {
      console.log(e)
    }
  },

  addMembershipItem ({ commit, state, rootGetters }, payload) {
    commit(types.RESET_MEMBERSHIP_ITEM)
    // payload is {voucher_id, member_voucher_id, item, quantity, options, toppings, set_course_items, final_price}
    const options = payload.options ? payload.options : []
    const toppings = payload.toppings ? payload.toppings : []
    const quantity = payload.voucher_id ? 1 : (payload.quantity ? payload.quantity : 1)
    const newItem = {
      cart_id: state.mcart.length === 0 ? 0 : Math.max(...state.mcart.map(i => i.cart_id)) + 1, // not needed for checkout
      voucher_id: payload.voucher_id || null,
      member_voucher_id: payload.member_voucher_id || null,
      item_id: payload.item.item_id,
      category_id: payload.item.category_id,
      category_name: payload.item.category_name,
      category_alternate_name: payload.item.category_alternate_name,
      name: payload.item.name,
      alternativeName: payload.item.alternate_name || '',
      portion_size_name: payload.item.portion_size_name || '',
      priceText: payload.item.price_text || '',
      plu_id: payload.item.plu_id,
      price: payload.item.price,
      price_override: payload.item.price_override || null,
      quantity: quantity,
      final_price: payload.final_price || payload.item.price, // not needed for checkout
      tax: payload.item.tax,
      service_charge: payload.item.service_charge,
      remarks: payload.special_req || '',
      time_required: payload.item.time_required || 0,
      tray_count: payload.item.tray_count,
      modifiers: options.concat(toppings),
      options: options, // not needed for checkout
      toppings: toppings, // not needed for checkout
      is_set_meal: payload.item.is_set_meal,
      set_course_items: payload.set_course_items || [],
      non_discount: payload.item.non_discount,
      photo_url: payload.item.photo_url,
      courses: payload.item.courses, // not needed for checkout
      option_groups: payload.item.option_groups, // not needed for checkout
      max_quantity: payload.item.max_quantity || 10, // not needed for checkout
      min_quantity: payload.item.min_quantity || 1, // not needed for checkout
      is_promo: payload.is_promo || false // not needed for checkout
    }
    commit(types.ADD_MEMBERSHIP_ITEM, newItem)
  },

  async checkFinalMembershipOrder ({ commit, state, rootState, rootGetters }, payload) {
    // payload is payments breakdown { points, credits, topay_payment }
    try {
      const items = state.mcart.map(item => {
        const tItem = Object.assign({}, item, {
          discount_id: item.preset_discount_id,
          options: [],
          toppings: []
        })
        return _.omit(tItem, ['cart_id', 'photo_url', 'max_quantity', 'min_quantity', 'is_promo'])
      })
      let paymentModeData = null
      if (state.paymentMode) {
        paymentModeData = state.paymentMode
      } else {
        if (rootGetters['config/paymentModes']) {
          paymentModeData = rootGetters['config/paymentModes'][0]
        }
      }
      let memberId, memberName, memberMobile
      const user = rootGetters['auth/user']
      const profile = rootGetters['member/profile']
      memberId = profile.loyalty_member_id
      memberName = user.name
      memberMobile = '+' + user.country_code + ' ' + user.mobile
      let deliveryCharge = 0
      let tableName, delivery, takeAwayAt, totTimeRequired
      const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
      tableName = taTable ? taTable.qr_code : ''
      delivery = null
      takeAwayAt = '0000-00-00 00:00:00'
      totTimeRequired = '0'
      const paymentTypeId = paymentModeData ? paymentModeData.type : null
      const restaurant = rootGetters['config/restaurant']
      // Select whether to apply cart discount, or dine mode's preset discount
      let discountInfo = {
        discount_id: '',
        discount_amount: '',
        discount_type: '',
        non_discount: ''
      }
      const order = {
        ...restaurant,
        payment_type_id: paymentTypeId,
        table_name: tableName,
        items: items,
        ...discountInfo,
        tot_time_required: totTimeRequired,
        delivery: delivery
      }
      const info = {
        device: rootGetters['config/posType'] === 0 ? rootGetters['config/device'] : 'jade',
        with_table: rootGetters['config/withTableNo'],
        is_take_away: 1,
        takeaway_at: takeAwayAt, // yyyy-mm-dd hh:mm:ss
        member_id: memberId,
        member_name: memberName,
        member_mobile: memberMobile,
        is_paid: 1,
        pay_later: 0
      }
      const params = {
        info: info,
        order: order,
        vouchers: '',
        payments: payload,
        delivery_charge: deliveryCharge
      }
      const { data } = await axios.post('/api/check', params)
      if (data.code === 1) {
        commit(types.CHECK_FINAL_MEMBERSHIP_ORDER, { ...data.order, reference_type: rootGetters['config/posType'] })
      }
    } catch (e) {
      console.log(e)
    }
  },
  async saveActiveOrderMembershipEpayClientRef ({ commit, state, rootState, rootGetters }) {
    try {
      const activeOrder = state.activeOrderMembership
      let memberId, userId, recipientName, recipientEmail, recipientContact, receiver, receiverContact
      const user = rootGetters['auth/user']
      const profile = rootGetters['member/profile']
      memberId = profile.loyalty_member_id
      userId = user.id
      recipientName = user.name
      recipientEmail = user.email
      recipientContact = '+ ' + user.country_code + ' ' + user.mobile
      receiver = ''
      receiverContact = ''
      let paymentModeData = null
      if (rootGetters['order/paymentMode']) {
        paymentModeData = rootGetters['order/paymentMode']
      } else {
        if (rootGetters['config/paymentModes']) {
          paymentModeData = rootGetters['config/paymentModes'][0]
        }
      }
      const paymentType = paymentModeData ? paymentModeData.code : null
      const config = rootGetters['config/config']
      await axios.post('/api/epay/client_ref/add', {
        member_id: memberId,
        reference_id: parseInt(activeOrder.reference_id),
        restaurant_id: parseInt(rootGetters['config/rid']),
        reference_type: 'activeOrder',
        content: {
          ...activeOrder,
          user_id: userId,
          member_id: memberId,
          reference_type: rootGetters['config/posType'],
          restaurant_id: parseInt(rootGetters['config/rid']),
          location: rootGetters['config/config'].location,
          payment_type: paymentType,
          pickup_time: '0000-00-00 00:00:00',
          tablename: null,
          address: null,
          is_refunded: 0
        },
        email_content: {
          receiptHeader: rootGetters['config/gemsSetting'].Header ? rootGetters['config/gemsSetting'].Header : '',
          storeName: config.full_name,
          recipientName: recipientName,
          recipientEmail: recipientEmail,
          recipientContact: recipientContact,
          receiver: receiver,
          receiver_contact: receiverContact,
          receiptBcc: rootGetters['config/config'].email_order_bcc,
          remarks: '',
          helpFooter: config.email_help_footer
        }
      })
    } catch (e) {
      console.log(e)
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/member.js